import PropTypes from "prop-types";
import React, { useCallback, useState, useEffect, useRef } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

const agencyAnalyticsUrl = "/interact/api/agency_analytics?company_id=:company_id&period=:period";
const companiesUrl = `${globalThis.Qwoted?.SUPABASE_API_EXTERNAL_URL || ""}/rest/v1/companies`;
const SEARCH_DEBOUNCE_MS = 300;

// Default agency entry when no analytics found
const createDefaultAgencyEntry = (company) => ({
  bad_behavior_points: 0,
  company_id: company.id,
  company_name: company.name,
  company_slug: company.slug,
  conversation_outcome_points: 0,
  direct_media_reply_points: 0,
  disabled_interactions_bad_behavior_points: 0,
  hidden: false,
  num_conversations: 0,
  num_direct_media_replies: 0,
  num_pitches: 0,
  num_profiles: 0,
  num_represented: 0,
  period_end: new Date().toISOString(),
  period_slug: "all_time",
  period_start: new Date().toISOString(),
  pitch_speed_points: 0,
  spam_flagged_count_bad_behavior_points: 0,
  suspected_leaker_dialog_enabled_count_bad_behavior_points: 0,
  total_points: 0,
  v1_company_id: null,
});

function AddAgencyModal({ show, onClose, onAdd, existingCompanyIds, listSize = 0 }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("last_quarter");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState("");
  const debounceTimer = useRef(null);
  const abortControllerRef = useRef(null);

  // Fetch and enrich agencies with analytics
  const performSearch = useCallback(
    async (query, period) => {
      if (!query.trim()) {
        setResults([]);
        setError(null);
        return;
      }

      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this search
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setLoading(true);
      setError(null);

      try {
        // Search companies from Supabase
        const companiesResponse = await fetch(`${companiesUrl}?name=ilike.*${query}*&limit=50`, {
          cache: "no-store",
          method: "GET",
          signal,
          headers: new Headers({
            ...authenticatedHeadersHash(),
            "Content-Type": "application/json",
          }),
        });

        if (!companiesResponse.ok) {
          throw new Error(`Failed to search companies: ${companiesResponse.status}`);
        }

        const companies = await companiesResponse.json();

        // Enrich each company with analytics and mark which are already added
        const enriched = await Promise.all(
          companies.map(async (company) => {
            const isAlreadyAdded = existingCompanyIds.includes(company.id);

            try {
              const analyticsResponse = await fetch(
                agencyAnalyticsUrl.replace(":company_id", company.id).replace(":period", period),
                {
                  cache: "no-store",
                  method: "GET",
                  signal,
                  headers: new Headers(authenticatedHeadersHash()),
                },
              );

              if (analyticsResponse.ok) {
                const analyticsData = await analyticsResponse.json();
                const analytics = analyticsData.data.agencies[0];
                if (analytics) {
                  return { ...analytics, isAlreadyAdded };
                }
              }
            } catch (e) {
              // Silently fail and use defaults (ignore AbortError)
              if (e.name !== "AbortError") {
                // Could log actual errors if needed, but for now silently fail
              }
            }

            // Return default entry if no analytics found
            return { ...createDefaultAgencyEntry(company), isAlreadyAdded };
          }),
        );

        setResults(enriched);
      } catch (err) {
        // Ignore AbortError (user cancelled by typing again)
        if (err.name !== "AbortError") {
          setError(`Failed to search: ${err.message}`);
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [existingCompanyIds],
  );

  // Debounced search on query change
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(searchQuery, selectedPeriod);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, selectedPeriod, performSearch]);

  // Close modal and reset state
  const handleClose = () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setSearchQuery("");
    setResults([]);
    setError(null);
    setPosition("");
    onClose();
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (!show) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Agency to List</h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-md-8">
                <div className="mb-3">
                  <label htmlFor="period-select" className="form-label">
                    Period
                  </label>
                  <select
                    id="period-select"
                    className="form-select"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                  >
                    <option value="this_quarter">This Quarter</option>
                    <option value="last_quarter">Last Quarter</option>
                    <option value="all_time">All Time</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="position-input" className="form-label">
                    Position
                  </label>
                  <input
                    id="position-input"
                    type="number"
                    className="form-control"
                    placeholder="Optional"
                    min="1"
                    max={listSize + 1}
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                  />
                  <small className="text-muted">1–{listSize + 1} (leave empty to add at end)</small>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="search-input" className="form-label">
                Search by Agency Name
              </label>
              <input
                id="search-input"
                type="text"
                className="form-control"
                placeholder="Enter agency name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {loading && <small className="text-muted">Searching...</small>}
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {results.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead className="thead-light">
                    <tr>
                      <th>Agency</th>
                      <th>Pitches</th>
                      <th>Total Points</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((agency) => (
                      <tr key={agency.company_id}>
                        <td>{agency.company_name}</td>
                        <td>{agency.num_pitches}</td>
                        <td>{agency.total_points}</td>
                        <td>
                          <button
                            type="button"
                            className={`btn btn-sm ${agency.isAlreadyAdded ? "btn-secondary" : "btn-primary"}`}
                            onClick={() => {
                              const positionValue = position ? parseInt(position, 10) : undefined;
                              onAdd(agency, positionValue);
                              handleClose();
                            }}
                            disabled={agency.isAlreadyAdded}
                          >
                            {agency.isAlreadyAdded ? "Already Added" : "Add"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && results.length === 0 && searchQuery && (
              <div className="alert alert-info" role="alert">
                No agencies found matching your search.
              </div>
            )}

            {!loading && results.length === 0 && !searchQuery && (
              <div className="text-muted text-center py-3">Enter a search term and click Search to find agencies.</div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

AddAgencyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingCompanyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  listSize: PropTypes.number,
};

export default AddAgencyModal;
